import React, { useState, useEffect, useMemo } from 'react'
import logger from '@adc/logger'
import {
  Modal,
  Icon,
  Button,
  Text,
  SelectField,
  MarkdownRenderer,
} from '@adc/ui-morpheus'
import { InputField, TextAreaField } from '@adc/ui-core'
import classnames from 'classnames/bind'
import type {
  CognitionEvaluationJudge,
  CognitionEvaluationTaskName,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import {
  fetchJudges,
  fetchJudge,
  createJudge,
  updateJudge,
} from '../../../providers/aiEvaluations'
import styles from './styles.scss'

const cx = classnames.bind(styles)

interface ManageLLMJudgesModalProps {
  isOpen: boolean
  onClose: () => void
  initialJudgeUrn?: string
  task: CognitionEvaluationTaskName
}

type ModalMode = 'view' | 'edit' | 'create'

const ManageLLMJudgesModal: React.FC<ManageLLMJudgesModalProps> = ({
  isOpen,
  onClose,
  initialJudgeUrn,
  task,
}): React.ReactElement => {
  const [judges, setJudges] = useState<CognitionEvaluationJudge[]>([])
  const [selectedJudgeUrn, setSelectedJudgeUrn] = useState<string>('')
  const [selectedJudge, setSelectedJudge] =
    useState<CognitionEvaluationJudge | null>(null)
  const [isLoadingJudges, setIsLoadingJudges] = useState(false)
  const [isLoadingJudge, setIsLoadingJudge] = useState(false)
  const [mode, setMode] = useState<ModalMode>('view')
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Create mode form fields
  const [editableDescription, setEditableDescription] = useState<string>('')
  const [editablePrompt, setEditablePrompt] = useState<string>('')

  // Filter judges to only include LLM judges (type = PROMPT)
  const llmJudges = useMemo(
    () =>
      judges.filter(
        judge => judge.type === graph.enums.CognitionEvaluationJudgeType.Prompt
      ),
    [judges]
  )

  // Transform judges into dropdown options
  const judgeOptions = useMemo(
    () =>
      llmJudges
        .map(judge => ({
          text: judge.description,
          value: judge.urn,
        }))
        .sort((a, b) => {
          const textA = (a.text || '').toLowerCase()
          const textB = (b.text || '').toLowerCase()
          return textA.localeCompare(textB)
        }),
    [llmJudges]
  )

  // Fetch all judges when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoadingJudges(true)
      fetchJudges(task)
        .then(data => {
          setJudges(data)
          return null
        })
        .catch(error => {
          logger.error('Failed to fetch judges:', error)
          setJudges([])
        })
        .finally(() => {
          setIsLoadingJudges(false)
        })
    }
  }, [isOpen, task])

  // Handle initialJudgeUrn - fetch and select if provided
  useEffect(() => {
    if (isOpen && initialJudgeUrn) {
      setSelectedJudgeUrn(initialJudgeUrn)
      setIsLoadingJudge(true)
      fetchJudge(initialJudgeUrn)
        .then(judge => {
          setSelectedJudge(judge)
          return null
        })
        .catch(error => {
          logger.error('Failed to fetch initial judge:', error)
          // Graceful degradation - reset to empty selection
          setSelectedJudgeUrn('')
          setSelectedJudge(null)
        })
        .finally(() => {
          setIsLoadingJudge(false)
        })
    }
  }, [isOpen, initialJudgeUrn])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setJudges([])
      setSelectedJudgeUrn('')
      setSelectedJudge(null)
      setIsLoadingJudges(false)
      setIsLoadingJudge(false)
      setMode('view')
      setIsDirty(false)
      setEditableDescription('')
      setEditablePrompt('')
      setShowDiscardConfirm(false)
    }
  }, [isOpen])

  // Handle judge selection from dropdown
  const handleJudgeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    meta?: { value: string }
  ) => {
    const value = meta?.value || event?.target?.value || ''
    setSelectedJudgeUrn(value)
    setIsLoadingJudge(true)
    fetchJudge(value)
      .then(judge => {
        setSelectedJudge(judge)
        return null
      })
      .catch(error => {
        logger.error('Failed to fetch judge details:', error)
        setSelectedJudge(null)
      })
      .finally(() => {
        setIsLoadingJudge(false)
      })
  }

  const handleClose = () => {
    onClose()
  }

  // Create mode handlers
  const handleCreateClick = () => {
    setEditableDescription('')
    setEditablePrompt('')
    setIsDirty(false)
    setMode('create')
  }

  const handleEditClick = () => {
    if (!selectedJudge) return

    // Pre-fill form with current judge data
    setEditableDescription(selectedJudge.description)
    setEditablePrompt(selectedJudge.judge_prompt || '')
    setIsDirty(false)
    setMode('edit')
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableDescription(e.target.value)
    setIsDirty(true)
  }

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditablePrompt(e.target.value)
    setIsDirty(true)
  }

  const handleCancel = () => {
    if (isDirty) {
      setShowDiscardConfirm(true)
    } else {
      setMode('view')
    }
  }

  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false)
    setMode('view')
    setEditableDescription('')
    setEditablePrompt('')
    setIsDirty(false)
  }

  const handleSave = () => {
    // Validation
    if (!editableDescription.trim() || !editablePrompt.trim()) {
      logger.error('Both description and prompt are required')
      return
    }

    setIsSaving(true)

    // Distinguish between create and edit mode
    const savePromise =
      mode === 'create'
        ? createJudge({
            description: editableDescription.trim(),
            judge_prompt: editablePrompt.trim(),
            task,
          })
        : updateJudge({
            urn: selectedJudgeUrn,
            description: editableDescription.trim(),
            judge_prompt: editablePrompt.trim(),
            task,
          })

    savePromise
      .then(updatedJudge => {
        // Switch to view mode
        setMode('view')
        setIsDirty(false)
        setEditableDescription('')
        setEditablePrompt('')

        // Select the judge (for create: new URN, for edit: same URN)
        setSelectedJudgeUrn(updatedJudge.urn)
        setSelectedJudge(updatedJudge)

        // Refetch judges list to include changes
        return fetchJudges(task)
      })
      .then(data => {
        if (data) {
          setJudges(data)
        }
        return null
      })
      .catch(error => {
        logger.error(`Failed to ${mode} judge:`, error)
        // Stay in create/edit mode on error
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const handleCloseModal = () => {
    if ((mode === 'create' || mode === 'edit') && isDirty) {
      setShowDiscardConfirm(true)
    } else {
      onClose()
    }
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        size='small'
        elmId='manage_llm_judges_modal'
        closeOnDimmerClick={false}
        closeIcon={
          <Icon
            className='close'
            name='times'
            iconStyle='regular'
            fitted
            onClick={handleClose}
          />
        }
        className={cx('modal')}
      >
        <Modal.Header>
          <Text size='large' weight='heavy'>
            {/* eslint-disable-next-line no-nested-ternary */}
            {mode === 'create'
              ? 'Create an LLM Judge'
              : mode === 'edit'
              ? 'Edit LLM Judge'
              : 'Manage LLM Judges'}
          </Text>
        </Modal.Header>

        <Modal.Content>
          <div className={cx('content-wrapper')}>
            {mode === 'view' ? (
              <>
                {/* VIEW MODE - existing code */}
                <div className={cx('judge-selector')}>
                  <Text size='small' color='gray' className={cx('label')}>
                    Judge
                  </Text>
                  <SelectField
                    value={selectedJudgeUrn}
                    onChange={handleJudgeChange}
                    options={judgeOptions}
                    placeholder='Select a Judge'
                    loading={isLoadingJudges}
                    elmId='judge_selector'
                    hideWrapper
                    compact
                    search
                  />
                </div>

                {isLoadingJudge && (
                  <div className={cx('loading-state')}>
                    <Icon
                      size='huge'
                      name='spinner'
                      iconStyle='light'
                      color='grayMedium'
                      loading
                    />
                    <Text color='grayMedium'>Loading judge details...</Text>
                  </div>
                )}

                {!isLoadingJudge &&
                  selectedJudge &&
                  selectedJudge.judge_prompt && (
                    <div className={cx('prompt-markdown')}>
                      <MarkdownRenderer content={selectedJudge.judge_prompt} />
                    </div>
                  )}

                {!isLoadingJudge &&
                  selectedJudge &&
                  !selectedJudge.judge_prompt && (
                    <div className={cx('empty-state')}>
                      <Text color='gray'>
                        This judge has no prompt configured
                      </Text>
                    </div>
                  )}

                {!selectedJudgeUrn && !isLoadingJudges && !isLoadingJudge && (
                  <div className={cx('empty-state')}>
                    <Text color='gray'>Select a judge to view its prompt</Text>
                  </div>
                )}
              </>
            ) : (
              <div className={cx('judge-form', 'ui form')}>
                {/* CREATE MODE */}
                <div className={cx('form-field')}>
                  <Text size='small' color='gray' className={cx('label')}>
                    Judge (Name)
                  </Text>
                  <InputField
                    elmId='judge_name_input'
                    value={editableDescription}
                    onChange={handleDescriptionChange}
                    placeholder='Enter judge name...'
                    fluid
                  />
                </div>

                <div className={cx('form-field')}>
                  <Text size='small' color='gray' className={cx('label')}>
                    Prompt
                  </Text>
                  <TextAreaField
                    elmId='judge_prompt_textarea'
                    value={editablePrompt}
                    onChange={handlePromptChange}
                    placeholder='Enter judge prompt...'
                    className={cx('prompt-textarea')}
                  />
                </div>
              </div>
            )}
          </div>
        </Modal.Content>

        <Modal.Actions>
          <div className={cx('actions')}>
            {mode === 'view' ? (
              <>
                <Button onClick={handleCloseModal} elmId='close_button'>
                  Close
                </Button>
                <Button onClick={handleCreateClick} elmId='create_button'>
                  Create
                </Button>
                <Button
                  onClick={handleEditClick}
                  role='primary'
                  elmId='edit_button'
                  disabled={!selectedJudgeUrn}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleCancel}
                  elmId='cancel_button'
                  size='small'
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  role='primary'
                  elmId='save_button'
                  size='small'
                  disabled={
                    !editableDescription.trim() || !editablePrompt.trim()
                  }
                  loading={isSaving}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </Modal.Actions>
      </Modal>

      {/* Discard Changes Confirmation Modal */}
      <Modal
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        size='tiny'
        elmId='discard_changes_modal'
      >
        <Modal.Header>
          <Text size='large' weight='heavy'>
            Discard Changes?
          </Text>
        </Modal.Header>
        <Modal.Content>
          <Text>Are you sure? All changes will be lost.</Text>
        </Modal.Content>
        <Modal.Actions>
          <div className={cx('actions')}>
            <Button
              onClick={() => setShowDiscardConfirm(false)}
              elmId='discard_cancel_button'
              size='small'
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDiscard}
              role='primary'
              elmId='discard_confirm_button'
              size='small'
            >
              Discard
            </Button>
          </div>
        </Modal.Actions>
      </Modal>
    </>
  )
}

export default ManageLLMJudgesModal
